import { useEffect, useRef } from 'react'
import { type LocationSearchResult } from '@/api/accommodation/types'
import { trackSearchResultsViewed, trackSearchNoResults, type SearchFilters } from '@/lib/track'

/**
 * Fires the results-viewed / no-results events once per settled data set.
 * Shared by the list and map views so both keep the search funnel complete.
 */
export function useSearchResultsTracking({
  data,
  results,
  filters,
  ready,
  view,
}: {
  data: unknown
  results: LocationSearchResult[]
  filters?: SearchFilters
  ready: boolean
  view: 'list' | 'map'
}): void {
  const lastTrackedDataRef = useRef<unknown>(null)

  useEffect(() => {
    if (!ready || lastTrackedDataRef.current === data) return

    if (results.length > 0) {
      lastTrackedDataRef.current = data
      trackSearchResultsViewed(results, filters, view)
    } else if (data && filters) {
      // Empty result set — capture the filters that led to it.
      lastTrackedDataRef.current = data
      trackSearchNoResults(filters, view)
    }
  }, [ready, data, results, filters, view])
}
